import { getValidator } from '../config/tourValidators';
import { TourStep, TourState } from '../types/TourTypes';
import { TourStatus } from '../enums/tourStatus';
import type { TourConfig } from '@/shared/services/api/SettingsService';
import type { UserSettings } from '@/features/Users/services/userSettingsService';

export class TourService {
  /**
   * Carrega a configuração do modal inicial
   */
  static loadInitialModalConfig(config: TourConfig) {
    return config.initialModal;
  }

  /**
   * Carrega as etapas do tour a partir da configuração
   */
  static loadSteps(config: TourConfig): TourStep[] {
    return config.steps
      .filter(step => step.enabled)
      .map(step => ({
        ...step,
        validator: getValidator(step.validatorKey)
      }))
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Carrega o estado inicial do tour baseado nos dados do usuário
   */
  static loadInitialState(userSettings?: UserSettings | null): TourState {
    const tourMetadata = userSettings?.metadata?.tour;

    return {
      isActive: false,
      hasSeenInitialModal: tourMetadata?.status === TourStatus.COMPLETED,
      hasStartedBefore: tourMetadata?.status === TourStatus.IN_PROGRESS || tourMetadata?.status === TourStatus.COMPLETED,
      currentStepId: null,
      completedSteps: [],
      isChecklistVisible: true
    };
  }

  /**
   * Verifica se deve mostrar o modal inicial automaticamente
   */
  static shouldShowInitialModal(userSettings?: UserSettings | null): boolean {
    const tourMetadata = userSettings?.metadata?.tour;
    return !tourMetadata || (!tourMetadata.status);
  }

  /**
   * Splash de boas-vindas pendente pós-onboarding de convidado (bug 84095):
   * o convidado entra numa org que já tem produtos, então o gate "org vazia"
   * do dashboard não dispara o modal — esta flag de sessão cobre esse caso.
   */
  private static readonly PENDING_WELCOME_TOUR_KEY = 'prodgy_pending_welcome_tour';

  static markWelcomeTourPending(): void {
    sessionStorage.setItem(TourService.PENDING_WELCOME_TOUR_KEY, '1');
  }

  static hasWelcomeTourPending(): boolean {
    return sessionStorage.getItem(TourService.PENDING_WELCOME_TOUR_KEY) === '1';
  }

  static consumeWelcomeTourPending(): void {
    sessionStorage.removeItem(TourService.PENDING_WELCOME_TOUR_KEY);
  }

  /**
   * Calcula steps completados baseado no step atual
   */
  static getCompletedSteps(currentStep: number, allSteps: TourStep[]): string[] {
    return allSteps
      .filter((_, index) => index < currentStep)
      .map(step => step.id);
  }

  /**
   * Limpa cache de sessão do tour
   */
  static clearSessionCache(): void {
    sessionStorage.removeItem('tour_message_sent');
    sessionStorage.removeItem('tour_integrations_initial_count');
    sessionStorage.removeItem('tour_agents_initial_count');
    sessionStorage.removeItem('tour_playground_interaction_time');
    sessionStorage.removeItem(TourService.PENDING_WELCOME_TOUR_KEY);
  }
}
